/**
 * ADR-225 §2–§3 — influencer link capture on the web.
 *
 * Every page load reads `eros_ref` from the address and keeps {ref, at} in localStorage
 * `eros_ref_v1` (the last link wins). The address is never touched, so GA4 still reads the
 * UTMs from page_location. The checkout sheet asks window.ErosAttribution.current() and puts
 * the answer on its quote and confirm payloads. A signed-in customer also sends one
 * POST /v1/attribution/touch {ref, at} per (ref, at), so an order placed later from another
 * device still counts. Rules (pure, fixture-tested): eros-attribution-rules.js.
 * Transport: window.ErosCommerce (its credentials:'include' carries the session). Failures are
 * silent, and every storage access is guarded: private mode never throws.
 */
(function (root) {
  'use strict';
  var R = root.ErosAttributionRules;
  if (root.ErosAttribution || !R) return;

  /* Private mode: storage may refuse writes; the entry then lives for this page only. */
  var memory = null;

  function readStored() {
    try {
      var stored = R.parseStored(root.localStorage.getItem(R.STORE_KEY));
      if (stored) return stored;
    } catch (e) {
      /* storage blocked: fall back to this page's memory */
    }
    return memory;
  }

  function writeStored(entry) {
    memory = null;
    try {
      root.localStorage.setItem(R.STORE_KEY, JSON.stringify(entry));
    } catch (e) {
      memory = entry;
    }
  }

  /** The {ref, at} the checkout and the touch may send, or null. */
  function current() {
    return R.toSend(readStored(), Date.now());
  }

  function capture() {
    var stored = readStored();
    var next = R.nextStored(stored, R.parse(root.location.href), Date.now());
    if (next && (!stored || next.ref !== stored.ref || next.at !== stored.at)) writeStored(next);
  }

  /* ---- touch on sign-in ---- */

  function customerOf() {
    var session = root.ErosSession && root.ErosSession.isSignedIn() ? root.ErosSession.get() : null;
    return session && typeof session.customerGid === 'string' ? session.customerGid : null;
  }

  function readSent() {
    try {
      return R.parseTouchRecord(root.localStorage.getItem(R.TOUCH_KEY));
    } catch (e) {
      return null;
    }
  }

  function writeSent(record) {
    try {
      root.localStorage.setItem(R.TOUCH_KEY, JSON.stringify(record));
    } catch (e) {
      /* storage blocked: the next page load may send it again; the server keeps one per day */
    }
  }

  var inFlight = null;

  function touch() {
    var customer = customerOf();
    var entry = current();
    if (!customer || !R.shouldTouch(entry, readSent(), customer)) return;
    var record = R.touchRecord(entry, customer);
    if (inFlight && inFlight.ref === record.ref && inFlight.at === record.at && inFlight.customer === customer) return;
    inFlight = record;
    root.ErosCommerce.request('/v1/attribution/touch', {
      method: 'POST',
      auth: true,
      body: { ref: entry.ref, at: entry.at }
    })
      .then(function () {
        writeSent(record);
      })
      .catch(function () {
        /* silent: the next load or sign-in tries again */
      })
      .then(function () {
        inFlight = null;
      });
  }

  function whenReady(fn, tries) {
    if (root.ErosCommerce && root.ErosSession) return fn();
    if ((tries || 0) > 50) return undefined;
    return setTimeout(function () {
      whenReady(fn, (tries || 0) + 1);
    }, 100);
  }

  root.ErosAttribution = { current: current };

  capture();
  whenReady(function () {
    /* onChange answers at once with the current state, then on every sign-in and sign-out. */
    if (root.ErosSession.onChange) root.ErosSession.onChange(touch);
    else touch();
  });
})(typeof window !== 'undefined' ? window : globalThis);
